import React, { useState, useEffect, useRef } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { createFocusTrap } from 'focus-trap';

type NodeType = 'agent' | 'task' | 'tool' | 'flow';

interface ClearCanvasModalProps {
  onClose: () => void;
  onConfirm: (options: { selectedOnly: boolean; nodeTypes: NodeType[] }) => void;
  nodeTypes: NodeType[];
  selectedNodesCount: number;
}

export function ClearCanvasModal({
  onClose,
  onConfirm,
  nodeTypes,
  selectedNodesCount
}: ClearCanvasModalProps) {
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<NodeType[]>([]);
  const modalRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Add class to body when modal is open
    document.body.classList.add('modal-open');

    // Create focus trap
    const trap = createFocusTrap(modalRef.current!, {
      initialFocus: '#cancel-button',
      fallbackFocus: '#clear-button',
      clickOutsideDeactivates: true,
      escapeDeactivates: true,
      onDeactivate: () => {
        setIsClosing(true);
        setTimeout(onClose, 200);
      }
    });

    // Activate trap after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      trap.activate();
    }, 100);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      trap.deactivate();
      document.body.classList.remove('modal-open');
    };
  }, [onClose]);

  const handleConfirm = () => {
    onConfirm({
      selectedOnly,
      nodeTypes: selectedTypes
    });
  };

  const toggleNodeType = (type: NodeType) => {
    setSelectedTypes((prev) =>
      prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type]
    );
  };

  return (
    <div className="fixed inset-0 z-[99999] overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-900/80 transition-opacity" />

        <div 
          ref={modalRef}
          className={`relative transform overflow-hidden rounded-lg bg-gray-800 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg ${
            isClosing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
          }`}
        >
          <div className="px-4 pb-4 pt-5 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              <h3 className="text-base font-semibold text-gray-100">
                Clear Canvas
              </h3>
            </div>

            {selectedNodesCount > 0 && (
              <div className="mb-4">
                <label className="flex items-center gap-2 text-gray-300">
                  <input
                    type="checkbox"
                    id="selectedOnly"
                    checked={selectedOnly}
                    onChange={(e) => setSelectedOnly(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-indigo-500 focus:ring-indigo-500"
                  />
                  <span className="text-sm">
                    Clear only selected nodes ({selectedNodesCount} selected)
                  </span>
                </label>
              </div>
            )}

            {!selectedOnly && nodeTypes.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm text-gray-300">Select node types to clear:</p>
                {nodeTypes.map((type) => (
                  <label key={type} className="flex items-center gap-2 text-gray-300">
                    <input
                      type="checkbox"
                      id={type}
                      checked={selectedTypes.includes(type)}
                      onChange={() => toggleNodeType(type)}
                      className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-indigo-500 focus:ring-indigo-500"
                    />
                    <span className="text-sm capitalize">{type} nodes</span>
                  </label>
                ))}
              </div>
            )}

            <div className="mt-4 text-sm text-yellow-500/80 bg-yellow-500/10 p-3 rounded-lg">
              {selectedOnly
                ? "This will remove all selected nodes and their connections."
                : selectedTypes.length > 0
                ? `This will remove all ${selectedTypes.join(", ")} nodes and their connections.`
                : "This will remove all nodes and connections from the canvas."}
            </div>
          </div>

          <div className="bg-gray-800 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              id="clear-button"
              onClick={handleConfirm}
              className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 sm:ml-3 sm:w-auto"
            >
              Clear
            </button>
            <button
              type="button"
              id="cancel-button"
              onClick={onClose}
              className="mt-3 inline-flex w-full justify-center rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-300 shadow-sm ring-1 ring-inset ring-gray-600 hover:bg-gray-600 sm:mt-0 sm:w-auto"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}